import imageCompression from "browser-image-compression";

/**
 * Default options for `browser-image-compression` (tweak per app / call `compressImageFile(file, { ... })`).
 * @type {import('browser-image-compression').Options & { maxSizeMB: number }}
 */
export const defaultCompressionOptions = {
  maxSizeMB: 1,
  maxWidthOrHeight: 1920,
  useWebWorker: true,
  initialQuality: 0.82,
  fileType: "image/jpeg",
  maxIteration: 10,
};

/**
 * Compress an image file in the browser (uses `browser-image-compression`).
 * Suitable before uploading to your API.
 *
 * @param {File} file
 * @param {object} [userOptions] – merged into library options. Supports legacy keys:
 *   `maxWidth`, `maxHeight` → combined into `maxWidthOrHeight` (max of the two);
 *   `quality` → `initialQuality`; `outputMimeType` → `fileType`;
 *   `maxSizeMB` caps output size.
 * @returns {Promise<File>}
 */
export async function compressImageFile(file, userOptions = {}) {
  if (!file || !String(file.type || "").startsWith("image/")) {
    throw new Error("Please choose an image file.");
  }

  const {
    maxWidth = 1920,
    maxHeight = 1920,
    quality = defaultCompressionOptions.initialQuality,
    outputMimeType = defaultCompressionOptions.fileType,
    maxSizeMB = defaultCompressionOptions.maxSizeMB,
    ...rest
  } = userOptions;

  const maxWidthOrHeight = Math.max(
    Number(maxWidth) || 1920,
    Number(maxHeight) || 1920
  );

  /** @type {import('browser-image-compression').Options} */
  const options = {
    ...defaultCompressionOptions,
    maxSizeMB,
    maxWidthOrHeight,
    initialQuality: quality,
    fileType: outputMimeType,
    useWebWorker: userOptions.useWebWorker !== false,
    ...rest,
  };

  const output = await imageCompression(file, options);

  if (output instanceof File) {
    return output;
  }

  const base =
    file.name?.replace(/\.[^.]+$/i, "")?.trim() || "image";
  const ext =
    outputMimeType === "image/png"
      ? ".png"
      : outputMimeType === "image/webp"
        ? ".webp"
        : ".jpg";

  return new File([output], `${base}${ext}`, {
    type: outputMimeType,
    lastModified: Date.now(),
  });
}
